import React, { useState, useEffect, useRef } from "react";
import api, { resolveBackendUrl } from "../../../services/api";
import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import Spinner from "../../../components/ui/Spinner";
import Modal from "../../../components/ui/Modal";
import FormField from "../../../components/ui/FormField";
import TextInput from "../../../components/ui/TextInput";
import { toast } from "react-toastify";
import {
  Paperclip,
  X,
  CheckCheck,
  Reply,
  ChevronLeft,
  ChevronRight,
  Download,
  Mic,
  Square,
  Trash2,
  Search,
  ChevronDown,
  Calendar,
  Edit2,
  FileText,
  Camera,
  Image as ImageIcon,
  Headphones,
  User as UserIcon,
  BarChart2,
  PhoneCall,
  Plus
} from "lucide-react";

import { getStoredAuthUser } from "../../../services/authService";

export default function CommunityMessages({ societyId, currentUser, onBack, embedded = false }) {
  const resolvedUser = currentUser || getStoredAuthUser();
  const currentUserId = resolvedUser?.user_id || resolvedUser?.id;
  const currentUserName = resolvedUser?.full_name || resolvedUser?.name || (resolvedUser?.role === 'super-admin' ? 'Super Admin' : 'Unknown User');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inputText, setInputText] = useState("");
  const [sending, setSending] = useState(false);

  // Pagination states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingOlder, setIsFetchingOlder] = useState(false);

  // NEW STATES
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [deletingMessageId, setDeletingMessageId] = useState(null);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [slideshowImages, setSlideshowImages] = useState([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(null);

  // Attachment Sheet & Modal States
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [showPollModal, setShowPollModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  // Poll Form State
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [pollAllowMultiple, setPollAllowMultiple] = useState(false);

  // Contact Form State
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  // Hidden File Input Refs
  const docInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const audioInputRef = useRef(null);

  // New Search and Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [showScrollDown, setShowScrollDown] = useState(false);

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const isInitialLoad = useRef(true);
  const lastMessageIdRef = useRef(null);

  // Audio Recording States
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);

  const fetchMessages = async (currentPage = 1, isLoadMore = false, container = null, previousScrollHeight = 0) => {
    if (!societyId) return;
    try {
      if (isLoadMore) setIsFetchingOlder(true);
      const res = await api.get(`/community/messages?society_id=${societyId}&page=${currentPage}&limit=50`);
      if (res.data?.messages) {
        const fetchedMessages = res.data.messages;
        if (fetchedMessages.length < 50) {
          setHasMore(false);
        }

        if (isLoadMore) {
          setMessages((prev) => {
            const existingIds = new Set(prev.map(m => m.id));
            const newFiltered = fetchedMessages.filter(m => !existingIds.has(m.id));
            return [...newFiltered, ...prev];
          });
          
          if (container) {
            setTimeout(() => {
              container.scrollTop = container.scrollHeight - previousScrollHeight;
            }, 0);
          }
        } else {
          setMessages((prev) => {
            if (currentPage === 1 && !isLoadMore && prev.length > 0) {
              const existingMessagesMap = new Map(prev.map(m => [m.id, m]));
              fetchedMessages.forEach(m => existingMessagesMap.set(m.id, m));
              return Array.from(existingMessagesMap.values()).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            }
            return fetchedMessages;
          });
        }
      }
    } catch (error) {
      console.error("Fetch Messages Error", error);
    } finally {
      setLoading(false);
      setIsFetchingOlder(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop <= 5 && hasMore && !isFetchingOlder) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchMessages(nextPage, true, e.target, scrollHeight);
    }

    if (scrollHeight - scrollTop - clientHeight > 100) {
      setShowScrollDown(true);
    } else {
      setShowScrollDown(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!societyId) return;
    setPage(1);
    setHasMore(true);
    fetchMessages(1, false);
    const interval = setInterval(() => {
      fetchMessages(1, false);
    }, 3000);
    return () => clearInterval(interval);
  }, [societyId]);

  const filteredMessages = React.useMemo(() => {
    return messages.filter((msg) => {
      let matchesSearch = true;
      let matchesDate = true;

      if (searchTerm) {
        let textContent = msg.content;
        try {
          const parsed = JSON.parse(msg.content);
          textContent = parsed.content || msg.content;
        } catch { /* ignore */ }
        matchesSearch = typeof textContent === 'string' && textContent.toLowerCase().includes(searchTerm.toLowerCase());
        if (!matchesSearch) {
          matchesSearch = msg.sender_name?.toLowerCase().includes(searchTerm.toLowerCase());
        }
      }

      if (selectedDate) {
        try {
          // Format the message date as YYYY-MM-DD in local time
          const dateObj = new Date(msg.created_at);
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          const msgDate = `${year}-${month}-${day}`;
          matchesDate = msgDate === selectedDate;
        } catch {
          matchesDate = true;
        }
      }

      return matchesSearch && matchesDate;
    });
  }, [messages, searchTerm, selectedDate]);

  const groupedMessages = React.useMemo(() => {
    const grouped = [];
    let currentGroup = null;

    filteredMessages.forEach((msg) => {
      let parsedData = {};
      let textContent = msg.content;
      try {
        parsedData = JSON.parse(msg.content);
        textContent = parsedData.content || msg.content;
      } catch {
        textContent = msg.content;
      }

      const isDeleted = Boolean(parsedData.is_deleted);
      const isEdited = Boolean(parsedData.is_edited);

      const parsedMsg = { ...msg, parsedData, textContent, isDeleted, isEdited };

      if (parsedMsg.message_type === 'image' && !isDeleted) {
        if (currentGroup && currentGroup.type === 'image_group' && currentGroup.sender_id === parsedMsg.sender_id) {
          currentGroup.images.push(parsedMsg);
        } else {
          currentGroup = {
            type: 'image_group',
            id: parsedMsg.id,
            sender_id: parsedMsg.sender_id,
            sender_name: parsedMsg.sender_name,
            created_at: parsedMsg.created_at,
            images: [parsedMsg],
          };
          grouped.push(currentGroup);
        }
      } else {
        currentGroup = parsedMsg;
        grouped.push(currentGroup);
      }
    });

    return grouped;
  }, [filteredMessages]);

  const allImages = React.useMemo(() => messages.filter(m => m.message_type === 'image').map(m => {
    let content = m.content;
    try { const p = JSON.parse(m.content); content = p.content || m.content; } catch { /* ignore */ }
    return resolveBackendUrl(content);
  }), [messages]);

  const openSlideshow = (imageUrl) => {
    // imageUrl is already resolved when clicked, but let's make sure
    const resolvedUrl = resolveBackendUrl(imageUrl);
    const idx = allImages.findIndex(img => img === resolvedUrl);
    if (idx !== -1) {
      setSlideshowImages(allImages);
      setCurrentSlideIndex(idx);
    }
  };

  useEffect(() => {
    if (messages.length === 0) return;

    const currentLastId = messages[messages.length - 1].id;

    if (isInitialLoad.current) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView(), 100);
      isInitialLoad.current = false;
      lastMessageIdRef.current = currentLastId;
      return;
    }

    // Only scroll if a NEW message arrived
    if (currentLastId !== lastMessageIdRef.current) {
      lastMessageIdRef.current = currentLastId;
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleStartEdit = (msg, currentText) => {
    setEditingMessage({
      id: msg.id,
      text: currentText || ""
    });
    setReplyingTo(null);
    setInputText(currentText || "");
    setTimeout(() => {
      const ta = document.getElementById("chat-textarea");
      if (ta) {
        ta.focus();
        ta.style.height = "auto";
        ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
      }
    }, 50);
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setInputText("");
    const ta = document.getElementById("chat-textarea");
    if (ta) ta.style.height = "auto";
  };

  const handleSaveEdit = async (e) => {
    e?.preventDefault?.();
    if (!editingMessage || !inputText.trim()) return;

    setSending(true);
    try {
      const res = await api.put(`/community/messages/${editingMessage.id}`, {
        content: inputText.trim()
      });

      if (res.data?.success && res.data?.updatedMessage) {
        const { id, content } = res.data.updatedMessage;
        setMessages((prev) =>
          prev.map((m) => (m.id === id ? { ...m, content: content } : m))
        );
        toast.success("Message edited");
        handleCancelEdit();
      }
    } catch (error) {
      console.error("Edit Message Error:", error);
      toast.error(error.response?.data?.message || "Failed to edit message");
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!messageId) return;
    const confirmDelete = window.confirm("Are you sure you want to delete this message?");
    if (!confirmDelete) return;

    setDeletingMessageId(messageId);
    try {
      const res = await api.delete(`/community/messages/${messageId}`);
      if (res.data?.success && res.data?.updatedMessage) {
        const { id, content } = res.data.updatedMessage;
        setMessages((prev) =>
          prev.map((m) => (m.id === id ? { ...m, content: content } : m))
        );
        toast.success("Message deleted");
      } else {
        // Fallback: update local message state to deleted
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  content: JSON.stringify({
                    content: "This message was deleted",
                    is_deleted: true,
                    deleted_at: new Date().toISOString()
                  })
                }
              : m
          )
        );
        toast.success("Message deleted");
      }
    } catch (error) {
      console.error("Delete Message Error:", error);
      toast.error(error.response?.data?.message || "Failed to delete message");
    } finally {
      setDeletingMessageId(null);
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (editingMessage) {
      return handleSaveEdit(e);
    }
    if (!inputText.trim()) return;

    setSending(true);

    // Construct payload matching Flutter app
    const payloadObj = { content: inputText };
    if (replyingTo) {
      payloadObj.replyToMessageId = replyingTo.id;
      payloadObj.replyToContent = typeof replyingTo.parsedContent === 'string' ? replyingTo.parsedContent : 'Attachment';
      payloadObj.replyToSenderName = replyingTo.sender_name;
    }

    const contentPayload = JSON.stringify(payloadObj);

    try {
      const res = await api.post("/community/messages", {
        society_id: societyId,
        message_type: "text",
        content: contentPayload,
        sender_id: currentUserId,
        sender_name: currentUserName,
      });

      if (res.data) {
        setMessages((prev) => [...prev, res.data]);
        setInputText("");
        setReplyingTo(null);
        
        const ta = document.getElementById("chat-textarea");
        if (ta) ta.style.height = "auto";
      }
    } catch (error) {
      console.error("Send Error", error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      console.error("Microphone access denied", err);
      toast.error("Could not access microphone.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerIntervalRef.current);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    clearInterval(timerIntervalRef.current);
    setAudioBlob(null);
    setRecordingTime(0);
  };
  
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const sendAudioMessage = async () => {
    if (!audioBlob) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("file", audioBlob, "voice-message.webm");
      
      const uploadRes = await api.post("/community/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      const audioUrl = uploadRes.data.url;
      
      const payloadObj = { content: audioUrl };
      if (replyingTo) {
        payloadObj.replyToMessageId = replyingTo.id;
        payloadObj.replyToContent = typeof replyingTo.parsedContent === 'string' ? replyingTo.parsedContent : 'Voice Message';
        payloadObj.replyToSenderName = replyingTo.sender_name;
      }
      
      const res = await api.post("/community/messages", {
        society_id: societyId,
        message_type: "audio",
        content: JSON.stringify(payloadObj),
        sender_id: currentUserId,
        sender_name: currentUserName,
      });

      if (res.data) {
        setMessages((prev) => [...prev, res.data]);
        cancelRecording();
        setReplyingTo(null);
      }
    } catch (error) {
      console.error("Audio Send Error", error);
      toast.error("Failed to send voice message");
    } finally {
      setSending(false);
    }
  };

  const handleUploadFileObject = async (file, forcedType = null) => {
    if (!file) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (societyId) {
        formData.append("society_id", societyId);
      }
      
      const uploadRes = await api.post("/community/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      const fileUrl = uploadRes.data.url;
      let msgType = forcedType || "document";
      let payloadContent = `${fileUrl}|${file.name}|${(file.size / 1024).toFixed(1)} KB`;

      if (!forcedType) {
        if (file.type.startsWith("image/")) {
          msgType = "image";
          payloadContent = fileUrl;
        } else if (file.type.startsWith("video/")) {
          msgType = "video";
          payloadContent = fileUrl;
        } else if (file.type.startsWith("audio/")) {
          msgType = "audio";
          payloadContent = fileUrl;
        }
      } else if (forcedType === "image" || forcedType === "audio" || forcedType === "video") {
        payloadContent = fileUrl;
      }
      
      const payloadObj = { content: payloadContent };
      if (replyingTo) {
        payloadObj.replyToMessageId = replyingTo.id;
        payloadObj.replyToContent = typeof replyingTo.parsedContent === 'string' ? replyingTo.parsedContent : 'Attachment';
        payloadObj.replyToSenderName = replyingTo.sender_name;
      }
      
      const res = await api.post("/community/messages", {
        society_id: societyId,
        message_type: msgType,
        content: JSON.stringify(payloadObj),
        sender_id: currentUserId,
        sender_name: currentUserName,
      });

      if (res.data) {
        setMessages((prev) => [...prev, res.data]);
        setReplyingTo(null);
      }
    } catch (error) {
      console.error("File Upload Error", error);
      toast.error("Failed to send attachment");
    } finally {
      setSending(false);
      setShowAttachmentModal(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await handleUploadFileObject(file);
    }
    e.target.value = null;
  };

  const handleCreatePoll = async (e) => {
    e.preventDefault();
    const q = pollQuestion.trim();
    if (!q) {
      toast.error("Please enter a poll question");
      return;
    }

    const validOptions = pollOptions.map(opt => opt.trim()).filter(Boolean);
    if (validOptions.length < 2) {
      toast.error("Please provide at least 2 poll options");
      return;
    }

    const optionsMap = {};
    validOptions.forEach(opt => {
      optionsMap[opt] = 0;
    });

    setSending(true);
    try {
      const payloadObj = {
        content: q,
        pollOptions: optionsMap,
        allowMultipleAnswers: pollAllowMultiple
      };
      if (replyingTo) {
        payloadObj.replyToMessageId = replyingTo.id;
        payloadObj.replyToContent = typeof replyingTo.parsedContent === 'string' ? replyingTo.parsedContent : 'Poll';
        payloadObj.replyToSenderName = replyingTo.sender_name;
      }

      const res = await api.post("/community/messages", {
        society_id: societyId,
        message_type: "poll",
        content: JSON.stringify(payloadObj),
        sender_id: currentUserId,
        sender_name: currentUserName,
      });

      if (res.data) {
        setMessages((prev) => [...prev, res.data]);
        setReplyingTo(null);
        setShowPollModal(false);
        setPollQuestion("");
        setPollOptions(["", ""]);
        setPollAllowMultiple(false);
        toast.success("Poll created successfully");
      }
    } catch (err) {
      console.error("Failed to create poll:", err);
      toast.error("Failed to create poll");
    } finally {
      setSending(false);
    }
  };

  const handleSendContact = async (e) => {
    e.preventDefault();
    const name = contactName.trim();
    const phone = contactPhone.trim();
    if (!name || !phone) {
      toast.error("Please provide both contact name and phone number");
      return;
    }

    setSending(true);
    try {
      const payloadObj = {
        content: `${name}|${phone}`
      };
      if (replyingTo) {
        payloadObj.replyToMessageId = replyingTo.id;
        payloadObj.replyToContent = typeof replyingTo.parsedContent === 'string' ? replyingTo.parsedContent : 'Contact';
        payloadObj.replyToSenderName = replyingTo.sender_name;
      }

      const res = await api.post("/community/messages", {
        society_id: societyId,
        message_type: "contact",
        content: JSON.stringify(payloadObj),
        sender_id: currentUserId,
        sender_name: currentUserName,
      });

      if (res.data) {
        setMessages((prev) => [...prev, res.data]);
        setReplyingTo(null);
        setShowContactModal(false);
        setContactName("");
        setContactPhone("");
        toast.success("Contact shared successfully");
      }
    } catch (err) {
      console.error("Failed to send contact:", err);
      toast.error("Failed to send contact");
    } finally {
      setSending(false);
    }
  };

  const chatContent = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: embedded ? '76vh' : '80vh', background: 'var(--color-page-bg)', borderRadius: '12px', padding: '0px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', overflow: 'hidden' }}>

        {/* Header with Search and Date Filter */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          {onBack ? (
            <button
              onClick={onBack}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-strong)', fontWeight: '600' }}
            >
              <ChevronLeft size={18} /> Back to Society Selection
            </button>
          ) : (
            <div style={{ fontWeight: '600', color: 'var(--color-text-strong)' }}>Community Chat</div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Search Input */}
            <div style={{ display: 'flex', alignItems: 'center', background: 'var(--color-page-bg)', borderRadius: '20px', padding: '4px 12px', border: '1px solid var(--color-border)' }}>
              <Search size={16} color="var(--color-muted)" />
              <input 
                type="text" 
                placeholder="Search messages..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ border: 'none', background: 'transparent', outline: 'none', padding: '4px 8px', fontSize: '13px', width: '120px' }}
              />
              {searchTerm && (
                 <X size={14} color="var(--color-muted)" style={{cursor: 'pointer'}} onClick={() => setSearchTerm('')} />
              )}
            </div>

            {/* Date Picker */}
            <div style={{ display: 'flex', alignItems: 'center', background: 'var(--color-page-bg)', borderRadius: '20px', padding: '4px 12px', border: '1px solid var(--color-border)' }}>
              <Calendar size={16} color="var(--color-muted)" style={{marginRight: '8px'}} />
              <input 
                type="date" 
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '13px', color: 'var(--color-text)' }}
              />
              {selectedDate && (
                 <X size={14} color="var(--color-muted)" style={{cursor: 'pointer', marginLeft: '8px'}} onClick={() => setSelectedDate('')} />
              )}
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div style={{ position: 'relative', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div ref={chatContainerRef} onScroll={handleScroll} style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><Spinner /></div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--color-muted)', marginTop: '20px' }}>No messages yet. Say hi!</div>
          ) : (
            groupedMessages.map((msg, index) => {
              const isGroup = msg.type === 'image_group';
              const parsedData = isGroup ? {} : msg.parsedData;
              const textContent = isGroup ? '' : msg.textContent;
              const isDeleted = Boolean(msg.isDeleted);
              const isEdited = Boolean(msg.isEdited);

              const amIAdmin = currentUserName?.toLowerCase().includes("admin") || currentUser?.role?.includes("admin");
              const isSenderAdmin = msg.sender_name?.toLowerCase().includes("admin") || msg.sender_name?.toLowerCase() === "management";
              const isMe = msg.sender_id === currentUserId || (isSenderAdmin && amIAdmin);

              const currentMsgDate = new Date(msg.created_at).toLocaleDateString();
              let showDateDivider = false;
              let dateDividerText = "";
              
              if (index === 0) {
                showDateDivider = true;
              } else {
                const prevMsgDate = new Date(groupedMessages[index - 1].created_at).toLocaleDateString();
                if (currentMsgDate !== prevMsgDate) {
                  showDateDivider = true;
                }
              }

              if (showDateDivider) {
                const today = new Date().toLocaleDateString();
                const yesterday = new Date(Date.now() - 86400000).toLocaleDateString();
                if (currentMsgDate === today) {
                  dateDividerText = "Today";
                } else if (currentMsgDate === yesterday) {
                  dateDividerText = "Yesterday";
                } else {
                  dateDividerText = new Date(msg.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
                }
              }

              return (
                <React.Fragment key={msg.id || index}>
                  {showDateDivider && (
                    <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0 16px 0' }}>
                      <div style={{ background: 'var(--color-border-soft)', padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', color: 'var(--color-muted)' }}>
                        {dateDividerText}
                      </div>
                    </div>
                  )}
                  <div
                    id={`msg-${msg.id}`}
                    style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isMe ? 'flex-end' : 'flex-start',
                    marginBottom: '16px'
                  }}
                  onMouseEnter={() => setHoveredMessageId(msg.id)}
                  onMouseLeave={() => setHoveredMessageId(null)}
                >

                  {/* Sender Name (Only for received) */}
                  {!isMe && (
                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--color-text-strong)', marginBottom: '4px', marginLeft: '4px' }}>
                      {msg.sender_name}
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: isMe ? 'row-reverse' : 'row' }}>

                    {/* Message Bubble */}
                    <div className="msg-bubble" style={{
                      maxWidth: msg.message_type === 'audio' ? '100%' : '70%',
                      minWidth: '50px',
                      background: isMe ? 'var(--color-border-soft)' : 'var(--color-surface)',
                      padding: '12px',
                      borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      border: '1px solid',
                      borderColor: isMe ? 'var(--color-overlay-15)' : 'var(--color-border)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                      color: 'var(--color-text)',
                      fontSize: '13px',
                      lineHeight: '1.4'
                    }}>

                      {/* Replied-To Section inside Bubble */}
                      {parsedData.replyToMessageId && (
                        <div 
                          onClick={() => {
                            const parentEl = document.getElementById(`msg-${parsedData.replyToMessageId}`);
                            if (parentEl) {
                              parentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              // Highlight effect
                              const bubble = parentEl.querySelector('.msg-bubble');
                              if (bubble) {
                                bubble.style.transition = 'filter 0.3s ease';
                                bubble.style.filter = 'brightness(0.85)';
                                setTimeout(() => {
                                  bubble.style.filter = 'none';
                                }, 1200);
                              }
                            }
                          }}
                          style={{
                            background: 'var(--color-overlay-15)',
                            borderRadius: '10px',
                            padding: '8px 12px',
                            marginBottom: '8px',
                            border: '1px solid var(--color-overlay-15)',
                            cursor: 'pointer'
                          }}
                        >
                          <div style={{ fontSize: '10px', fontWeight: 'bold', color: 'var(--color-text-strong)', marginBottom: '2px' }}>
                            {parsedData.replyToSenderName || 'User'}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--color-text)', opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {parsedData.replyToContent || 'Message'}
                          </div>
                        </div>
                      )}

                      {/* Message Content Render */}
                      {isDeleted ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontStyle: 'italic', color: 'var(--color-muted)', fontSize: '13px' }}>
                          <Trash2 size={14} style={{ opacity: 0.6 }} />
                          <span>This message was deleted</span>
                        </div>
                      ) : (
                        <>
                          {msg.type === 'image_group' && (
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: msg.images.length > 1 ? 'repeat(2, 1fr)' : '1fr',
                              gap: '4px',
                              marginBottom: '8px'
                            }}>
                              {msg.images.slice(0, 4).map((imgMsg, idx) => {
                                const isLast = idx === 3 && msg.images.length > 4;
                                const remaining = msg.images.length - 4;
                                return (
                                  <div
                                    key={imgMsg.id}
                                    style={{ position: 'relative', cursor: 'pointer', aspectRatio: '1', overflow: 'hidden', borderRadius: '8px' }}
                                    onClick={() => openSlideshow(imgMsg.textContent)}
                                  >
                                    <img
                                      src={resolveBackendUrl(imgMsg.textContent)}
                                      alt="Attachment"
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    {isLast && (
                                      <div style={{
                                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                        background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: 'var(--color-surface)', fontSize: '18px', fontWeight: 'bold'
                                      }}>
                                        +{remaining}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {!isGroup && msg.message_type === 'image' && (
                            <div style={{ marginBottom: '8px', cursor: 'pointer' }} onClick={() => openSlideshow(textContent)}>
                              <img src={resolveBackendUrl(textContent)} alt="Attachment" style={{ borderRadius: '8px', maxWidth: '100%', maxHeight: '200px', objectFit: 'cover' }} />
                            </div>
                          )}

                          {msg.message_type === 'audio' && (
                            <div style={{ marginBottom: '8px' }}>
                              <audio controls src={resolveBackendUrl(textContent)} style={{ width: '100%', minWidth: '250px', height: '32px' }} />
                            </div>
                          )}

                          {msg.message_type === 'video' && (
                            <div style={{ marginBottom: '8px' }}>
                              <video controls src={resolveBackendUrl(textContent)} style={{ maxWidth: '100%', maxHeight: '280px', borderRadius: '8px' }} />
                            </div>
                          )}

                          {msg.message_type === 'poll' && (
                            <div style={{ marginBottom: '8px', background: 'var(--color-overlay-12)', padding: '10px', borderRadius: '8px' }}>
                              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>{textContent}</div>
                              {Object.entries(parsedData.pollOptions || {}).map(([opt, count], i) => (
                                <div key={i} style={{ padding: '6px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '6px', marginBottom: '4px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                                  <span>{opt}</span>
                                  <span style={{ color: 'var(--color-muted)', fontSize: '12px' }}>{count} votes</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {msg.message_type === 'document' && (() => {
                            const parts = textContent.split('|');
                            const url = parts[0] || '';
                            const fileName = parts.length > 1 ? parts[1] : 'Document';
                            const fileSize = parts.length > 2 ? parts[2] : '';

                            return (
                              <div
                                style={{ display: 'flex', alignItems: 'center', gap: '12px', background: isMe ? 'var(--color-overlay-40)' : 'var(--color-overlay-12)', padding: '12px', borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.05)' }}
                                onClick={() => window.open(resolveBackendUrl(url), '_blank')}
                              >
                                <div style={{ background: isMe ? 'var(--color-dark-bg)' : 'var(--color-border)', color: isMe ? 'white' : 'var(--color-text-strong)', padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <Paperclip size={24} />
                                </div>
                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                  <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {fileName}
                                  </div>
                                  <div style={{ fontSize: '11px', color: 'var(--color-muted)', marginTop: '2px' }}>
                                    {fileSize}
                                  </div>
                                </div>
                                <Download size={18} color="var(--color-text-strong)" />
                              </div>
                            );
                          })()}

                          {msg.message_type === 'contact' && (() => {
                            const parts = textContent.split('|');
                            const name = parts[0] || 'Unknown';
                            const phone = parts.length > 1 ? parts[1] : '';
                            const initial = name ? name[0].toUpperCase() : '?';

                            return (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  background: isMe ? 'var(--color-overlay-40)' : 'var(--color-overlay-12)',
                                  padding: '12px 14px',
                                  borderRadius: '10px',
                                  border: '1px solid rgba(0,0,0,0.05)',
                                  minWidth: '240px'
                                }}
                              >
                                <div
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    background: isMe ? 'var(--color-dark-bg)' : '#3b82f6',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '16px'
                                  }}
                                >
                                  {initial}
                                </div>
                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                  <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {name}
                                  </div>
                                  <div style={{ fontSize: '12px', color: 'var(--color-muted)', marginTop: '2px' }}>
                                    {phone}
                                  </div>
                                </div>
                                {phone && (
                                  <a
                                    href={`tel:${phone.replace(/[^\d+]/g, '')}`}
                                    style={{
                                      background: 'var(--color-surface)',
                                      border: '1px solid var(--color-border)',
                                      borderRadius: '50%',
                                      padding: '8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: '#10b981',
                                      textDecoration: 'none'
                                    }}
                                    title="Call contact"
                                  >
                                    <PhoneCall size={16} />
                                  </a>
                                )}
                              </div>
                            );
                          })()}

                          {(msg.message_type === 'text' || !['image', 'audio', 'video', 'poll', 'document', 'contact', 'image_group'].includes(msg.message_type)) && (
                            <div style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {textContent}
                            </div>
                          )}
                        </>
                      )}

                      {/* Timestamp, Edited Tag & Ticks */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '6px', gap: '4px' }}>
                        {isEdited && !isDeleted && (
                          <span style={{
                            fontSize: '9px',
                            fontWeight: '600',
                            padding: '1px 5px',
                            borderRadius: '4px',
                            background: 'var(--color-overlay-15)',
                            color: 'var(--color-muted)',
                            marginRight: '2px'
                          }}>
                            Edited
                          </span>
                        )}
                        <span style={{ fontSize: '9px', color: 'var(--color-muted)', fontWeight: '600' }}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {isMe && !isDeleted && <CheckCheck size={12} color="var(--color-text-strong)" />}
                      </div>
                    </div>

                    {/* Hover Actions (Reply, Edit, Delete) */}
                    {!isDeleted && (
                      <div style={{
                        opacity: hoveredMessageId === msg.id ? 1 : 0,
                        transition: 'opacity 0.15s ease-in-out',
                        pointerEvents: hoveredMessageId === msg.id ? 'auto' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <button
                          onClick={() => setReplyingTo({ ...msg, parsedContent: textContent })}
                          style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '50%', padding: '6px', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          title="Reply"
                        >
                          <Reply size={14} color="var(--color-muted)" />
                        </button>

                        {/* Edit Action (Only author or admin, for editable text messages) */}
                        {isMe && (msg.message_type === 'text' || !['image', 'audio', 'video', 'poll', 'document', 'image_group'].includes(msg.message_type)) && (
                          <button
                            onClick={() => handleStartEdit(msg, textContent)}
                            style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '50%', padding: '6px', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            title="Edit message"
                          >
                            <Edit2 size={13} color="var(--color-muted)" />
                          </button>
                        )}

                        {/* Delete Action (Author or Admin) */}
                        {(isMe || amIAdmin) && (
                          <button
                            onClick={() => handleDeleteMessage(msg.id)}
                            disabled={deletingMessageId === msg.id}
                            style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '50%', padding: '6px', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            title="Delete message"
                          >
                            <Trash2 size={13} color="var(--color-danger, #e11d48)" />
                          </button>
                        )}
                      </div>
                    )}

                  </div>
                </div>
                </React.Fragment>
              );
            })
          )}
          <div ref={messagesEndRef} />
          </div>
          {showScrollDown && (
            <button
              onClick={scrollToBottom}
              style={{
                position: 'absolute',
                bottom: '20px',
                right: '20px',
                background: 'var(--color-dark-bg)',
                color: 'var(--color-surface)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 10
              }}
            >
              <ChevronDown size={24} />
            </button>
          )}
        </div>

        {/* Input Area */}
        <div style={{ background: 'var(--color-surface)', padding: '16px', borderTop: '1px solid var(--color-border)' }}>

          {/* Editing Preview Banner */}
          {editingMessage && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--color-page-bg)', padding: '10px 16px', borderRadius: '8px', marginBottom: '12px', borderLeft: '4px solid var(--color-primary, #0ea5e9)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Edit2 size={16} color="var(--color-primary, #0ea5e9)" />
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-text-strong)' }}>
                    Editing message
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--color-muted)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px' }}>
                    {editingMessage.text}
                  </div>
                </div>
              </div>
              <button onClick={handleCancelEdit} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }} title="Cancel edit">
                <X size={18} color="var(--color-muted)" />
              </button>
            </div>
          )}

          {/* Reply Preview */}
          {replyingTo && !editingMessage && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--color-page-bg)', padding: '10px 16px', borderRadius: '8px', marginBottom: '12px', borderLeft: '4px solid var(--color-dark-bg)' }}>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-text-strong)' }}>
                  Replying to {replyingTo.sender_name}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--color-muted)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px' }}>
                  {typeof replyingTo.parsedContent === 'string' ? replyingTo.parsedContent : 'Attachment'}
                </div>
              </div>
              <button onClick={() => setReplyingTo(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={18} color="var(--color-muted)" />
              </button>
            </div>
          )}

          <style>
            {`
              @keyframes blink {
                0% { opacity: 1; }
                50% { opacity: 0; }
                100% { opacity: 1; }
              }
            `}
          </style>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {audioBlob ? (
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1 }}>
                <button onClick={cancelRecording} style={{ background: 'rgba(225, 29, 72, 0.1)', border: 'none', padding: '10px', borderRadius: '50%', cursor: 'pointer', color: 'var(--color-danger)' }} disabled={sending}>
                  <Trash2 size={20} />
                </button>
                <div style={{ flex: 1, background: 'var(--color-page-bg)', borderRadius: '24px', padding: '8px 16px', display: 'flex', alignItems: 'center' }}>
                  <audio controls src={URL.createObjectURL(audioBlob)} style={{ width: '100%', height: '32px' }} />
                </div>
                <Button onClick={sendAudioMessage} disabled={sending}>
                  {sending ? <Spinner /> : "Send"}
                </Button>
              </div>
            ) : isRecording ? (
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1 }}>
                <button onClick={cancelRecording} style={{ background: 'rgba(225, 29, 72, 0.1)', border: 'none', padding: '10px', borderRadius: '50%', cursor: 'pointer', color: 'var(--color-danger)' }}>
                  <Trash2 size={20} />
                </button>
                <div style={{ flex: 1, background: 'rgba(225, 29, 72, 0.1)', borderRadius: '24px', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: 'var(--color-danger)', fontWeight: 'bold' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--color-danger)', animation: 'blink 1s infinite' }}></div>
                  Recording {formatTime(recordingTime)}
                </div>
                <button onClick={stopRecording} style={{ background: 'var(--color-dark-bg)', border: 'none', padding: '10px', borderRadius: '50%', cursor: 'pointer', color: 'var(--color-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Square size={20} fill="var(--color-surface)" />
                </button>
              </div>
            ) : (
              <form onSubmit={handleSend} style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1 }}>
                {!editingMessage && (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowAttachmentModal(true)}
                      disabled={sending}
                      style={{
                        cursor: 'pointer',
                        padding: '8px',
                        background: 'var(--color-page-bg)',
                        border: 'none',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--color-muted)'
                      }}
                      title="Add attachment"
                    >
                      <Paperclip size={20} />
                    </button>

                    {/* Hidden Native File Pickers */}
                    <input
                      type="file"
                      ref={docInputRef}
                      style={{ display: 'none' }}
                      accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.csv"
                      onChange={handleFileUpload}
                    />
                    <input
                      type="file"
                      ref={cameraInputRef}
                      style={{ display: 'none' }}
                      accept="image/*"
                      capture="environment"
                      onChange={handleFileUpload}
                    />
                    <input
                      type="file"
                      ref={galleryInputRef}
                      style={{ display: 'none' }}
                      accept="image/*,video/*"
                      onChange={handleFileUpload}
                    />
                    <input
                      type="file"
                      ref={audioInputRef}
                      style={{ display: 'none' }}
                      accept="audio/*"
                      onChange={handleFileUpload}
                    />
                  </>
                )}

                <div style={{ flex: 1 }}>
                  <textarea
                    id="chat-textarea"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (inputText.trim() && !sending) {
                          handleSend(e);
                        }
                      } else if (e.key === 'Escape' && editingMessage) {
                        handleCancelEdit();
                      }
                    }}
                    placeholder={editingMessage ? "Edit message (Press Enter to save, Esc to cancel)..." : "Type a secure message..."}
                    disabled={sending}
                    rows={1}
                    style={{
                      width: '100%',
                      border: '1px solid var(--color-border)',
                      borderRadius: '12px',
                      padding: '10px 12px',
                      fontSize: '13px',
                      resize: 'none',
                      outline: 'none',
                      fontFamily: 'inherit',
                      maxHeight: '120px',
                      background: sending ? 'var(--color-page-bg)' : 'var(--color-surface)',
                      color: sending ? 'var(--color-muted)' : 'var(--color-text)'
                    }}
                    onInput={(e) => {
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                    }}
                  />
                </div>

                {editingMessage ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={sending}
                      style={{
                        background: 'transparent',
                        border: '1px solid var(--color-border)',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        color: 'var(--color-text)'
                      }}
                    >
                      Cancel
                    </button>
                    <Button type="submit" disabled={sending || !inputText.trim()}>
                      {sending ? <Spinner /> : "Save"}
                    </Button>
                  </div>
                ) : !inputText.trim() ? (
                  <button type="button" onClick={startRecording} disabled={sending} style={{ background: 'var(--color-page-bg)', border: 'none', padding: '10px', borderRadius: '50%', cursor: 'pointer', color: 'var(--color-text-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Mic size={20} />
                  </button>
                ) : (
                  <Button type="submit" disabled={sending}>
                    {sending ? <Spinner /> : "Send"}
                  </Button>
                )}
              </form>
            )}
          </div>
        </div>

      </div>

      {/* Full Screen Slideshow Modal */}
      {currentSlideIndex !== null && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.9)', zIndex: 9999,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
        }}>
          {/* Header Actions */}
          <div style={{ position: 'absolute', top: '20px', right: '20px', display: 'flex', gap: '16px' }}>
            <button
              onClick={() => {
                const link = document.createElement('a');
                link.href = slideshowImages[currentSlideIndex];
                link.download = `image_${currentSlideIndex}.jpg`;
                link.click();
              }}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-surface)' }}
              title="Download"
            >
              <Download size={24} />
            </button>
            <button
              onClick={() => setCurrentSlideIndex(null)}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-surface)' }}
              title="Close"
            >
              <X size={24} />
            </button>
          </div>

          {/* Nav & Image */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '0 20px' }}>
            <button
              onClick={() => setCurrentSlideIndex(prev => prev > 0 ? prev - 1 : slideshowImages.length - 1)}
              style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', padding: '12px', cursor: 'pointer', color: 'var(--color-surface)' }}
            >
              <ChevronLeft size={32} />
            </button>

            <img
              src={slideshowImages[currentSlideIndex]}
              alt="Slideshow"
              style={{ maxWidth: '80%', maxHeight: '80vh', objectFit: 'contain' }}
            />

            <button
              onClick={() => setCurrentSlideIndex(prev => prev < slideshowImages.length - 1 ? prev + 1 : 0)}
              style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', padding: '12px', cursor: 'pointer', color: 'var(--color-surface)' }}
            >
              <ChevronRight size={32} />
            </button>
          </div>

          {/* Counter */}
          <div style={{ position: 'absolute', bottom: '20px', color: 'var(--color-surface)', fontSize: '14px', background: 'rgba(0,0,0,0.5)', padding: '6px 12px', borderRadius: '16px' }}>
            {currentSlideIndex + 1} / {slideshowImages.length}
          </div>
        </div>
      )}

      {/* Attachment Selection Modal */}
      <Modal
        isOpen={showAttachmentModal}
        onClose={() => setShowAttachmentModal(false)}
        title="Share Content"
        size="md"
      >
        <div style={{ padding: '8px 0' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '20px',
            textAlign: 'center',
            padding: '12px 8px'
          }}>
            {/* Document */}
            <div
              onClick={() => {
                setShowAttachmentModal(false);
                setTimeout(() => docInputRef.current?.click(), 100);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}
            >
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: '#673ab7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 4px 10px rgba(103, 58, 183, 0.3)',
                transition: 'transform 0.15s'
              }}>
                <FileText size={26} />
              </div>
              <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--color-text)' }}>Document</span>
            </div>

            {/* Camera */}
            <div
              onClick={() => {
                setShowAttachmentModal(false);
                setTimeout(() => cameraInputRef.current?.click(), 100);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}
            >
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: '#e91e63',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 4px 10px rgba(233, 30, 99, 0.3)',
                transition: 'transform 0.15s'
              }}>
                <Camera size={26} />
              </div>
              <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--color-text)' }}>Camera</span>
            </div>

            {/* Gallery */}
            <div
              onClick={() => {
                setShowAttachmentModal(false);
                setTimeout(() => galleryInputRef.current?.click(), 100);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}
            >
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: '#ab47bc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 4px 10px rgba(171, 71, 188, 0.3)',
                transition: 'transform 0.15s'
              }}>
                <ImageIcon size={26} />
              </div>
              <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--color-text)' }}>Gallery</span>
            </div>

            {/* Audio */}
            <div
              onClick={() => {
                setShowAttachmentModal(false);
                setTimeout(() => audioInputRef.current?.click(), 100);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}
            >
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: '#ff9800',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 4px 10px rgba(255, 152, 0, 0.3)',
                transition: 'transform 0.15s'
              }}>
                <Headphones size={26} />
              </div>
              <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--color-text)' }}>Audio</span>
            </div>

            {/* Contact */}
            <div
              onClick={() => {
                setShowAttachmentModal(false);
                setShowContactModal(true);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}
            >
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: '#2196f3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 4px 10px rgba(33, 150, 243, 0.3)',
                transition: 'transform 0.15s'
              }}>
                <UserIcon size={26} />
              </div>
              <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--color-text)' }}>Contact</span>
            </div>

            {/* Poll */}
            <div
              onClick={() => {
                setShowAttachmentModal(false);
                setShowPollModal(true);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}
            >
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: '#009688',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 4px 10px rgba(0, 150, 136, 0.3)',
                transition: 'transform 0.15s'
              }}>
                <BarChart2 size={26} />
              </div>
              <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--color-text)' }}>Poll</span>
            </div>
          </div>
        </div>
      </Modal>

      {/* Create Poll Modal */}
      <Modal
        isOpen={showPollModal}
        onClose={() => setShowPollModal(false)}
        title="Create a Poll"
        size="md"
      >
        <form onSubmit={handleCreatePoll} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <FormField label="Question" required>
            <TextInput
              placeholder="Ask a question..."
              value={pollQuestion}
              onChange={(e) => setPollQuestion(e.target.value)}
              required
            />
          </FormField>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--color-text)' }}>
              Options (2 to 6)
            </label>
            {pollOptions.map((opt, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <TextInput
                  placeholder={`Option ${idx + 1}`}
                  value={opt}
                  onChange={(e) => {
                    const newOpts = [...pollOptions];
                    newOpts[idx] = e.target.value;
                    setPollOptions(newOpts);
                  }}
                  required={idx < 2}
                />
                {pollOptions.length > 2 && (
                  <button
                    type="button"
                    onClick={() => {
                      setPollOptions(pollOptions.filter((_, i) => i !== idx));
                    }}
                    style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px',
                      color: '#ef4444',
                      cursor: 'pointer'
                    }}
                    title="Remove option"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}

            {pollOptions.length < 6 && (
              <button
                type="button"
                onClick={() => setPollOptions([...pollOptions, ""])}
                style={{
                  alignSelf: 'flex-start',
                  background: 'none',
                  border: '1px dashed var(--color-border)',
                  padding: '6px 14px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: 'var(--color-primary, #0ea5e9)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Plus size={14} /> Add Option
              </button>
            )}
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', marginTop: '4px' }}>
            <input
              type="checkbox"
              checked={pollAllowMultiple}
              onChange={(e) => setPollAllowMultiple(e.target.checked)}
            />
            <span>Allow multiple answers</span>
          </label>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPollModal(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={sending}
            >
              {sending ? <Spinner /> : "Create Poll"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Share Contact Modal */}
      <Modal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        title="Share Contact"
        size="md"
      >
        <form onSubmit={handleSendContact} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <FormField label="Contact Name" required>
            <TextInput
              placeholder="e.g. John Doe / Security Desk"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              required
            />
          </FormField>

          <FormField label="Phone Number" required>
            <TextInput
              placeholder="e.g. +91 98765 43210"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              required
            />
          </FormField>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowContactModal(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={sending}
            >
              {sending ? <Spinner /> : "Share Contact"}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );

  if (embedded) {
    return chatContent;
  }

  return (
    <AdminLayout title="Community Chat (API Moderated)">
      {chatContent}
    </AdminLayout>
  );
}
